import React from "react";
import PropTypes from "prop-types";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Tooltip from "@mui/material/Tooltip";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SoftBox from "../../../components/SoftBox";
import {useTranslation} from "react-i18next";

// theme grey[500]: keeps the unavailable formats legible but clearly inactive
const DISABLED_COLOR = "#adb5bd";

/**
 * Output formats selection. A format is only selectable when it is compatible
 * with the selected OCR mode (see `compatible_output_formats` of each OCR mode).
 */
const OutputFormats = ({
                           label,
                           hint,
                           outputFormats,
                           ocrModes,
                           selectedOcrMode,
                           selectedOutputFormats,
                           onToggle
                       }) => {
    const { t, i18n } = useTranslation("publicationsTranslation");
    const isRtl = i18n.dir() === "rtl";

    const selectedMode = ocrModes.find(mode => mode.id === selectedOcrMode);
    const compatibleIds = selectedMode?.compatible_output_formats ?? [];

    const getUnavailableReason = (formatId) => {
        const modes = ocrModes
            .filter(mode => mode.compatible_output_formats?.includes(formatId))
            .map(mode => mode.slug)
            .join(", ");
        return modes ? t("outputFormatAvailableWith", { modes }) : t("outputFormatUnavailable");
    };

    return (
        <React.Fragment>
            <label htmlFor="output-formats" className="label-style">
                {label}
            </label>
            <label style={{fontSize: "11px", color: "#3f4a63"}}>{hint}</label>

            <SoftBox style={{display: "grid", gridTemplateColumns: "50% 50%", gap: "0.2rem"}}>
                {outputFormats.map(format => {
                    const isCompatible = compatibleIds.includes(format.id);
                    const labelMarginSide = isRtl ? "marginRight" : "marginLeft";
                    const formatControl = (
                        <FormControlLabel
                            style={{margin: "0px", fontWeight: 400}}
                            disabled={!isCompatible}
                            control={
                                <Checkbox
                                    checked={isCompatible && selectedOutputFormats.includes(format.id)}
                                    onChange={() => onToggle(format.id)}
                                    color="primary"
                                />
                            }
                            label={
                                <span style={{display: "inline-flex", alignItems: "center", gap: "4px"}}>
                                    {/* Colour set here rather than on the disabled label class: SoftBox
                                        and MUI both set an explicit colour that would win over it. */}
                                    <span style={{
                                        fontWeight: 400,
                                        color: isCompatible ? undefined : DISABLED_COLOR
                                    }}>
                                        {format.slug}
                                    </span>
                                    {/* Reinforces the greyed-out state: the tooltip alone is not
                                        discoverable on touch devices. */}
                                    {!isCompatible && (
                                        <LockOutlinedIcon sx={{fontSize: "0.85rem", color: DISABLED_COLOR}}/>
                                    )}
                                </span>
                            }
                            sx={{
                                margin: 0,
                                '& .MuiFormControlLabel-label': { [labelMarginSide]: "8px" },
                                ...(!isCompatible && {
                                    '& .MuiCheckbox-root.Mui-disabled': { color: DISABLED_COLOR }
                                })
                            }}
                            labelPlacement={isRtl ? "start" : "end"}
                        />
                    );

                    return (
                        <div key={format.id} style={{display: "flex", alignItems: "center"}}>
                            {isCompatible ? formatControl : (
                                <Tooltip title={getUnavailableReason(format.id)} placement="top" arrow>
                                    {/* Wrapper required: disabled elements don't fire tooltip events */}
                                    <span style={{cursor: "not-allowed"}}>{formatControl}</span>
                                </Tooltip>
                            )}
                        </div>
                    );
                })}
            </SoftBox>
        </React.Fragment>
    );
};

OutputFormats.propTypes = {
    label: PropTypes.string,
    hint: PropTypes.string,
    outputFormats: PropTypes.array.isRequired,
    ocrModes: PropTypes.array.isRequired,
    selectedOcrMode: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    selectedOutputFormats: PropTypes.array.isRequired,
    onToggle: PropTypes.func.isRequired,
};

export default OutputFormats;
